import { create } from "zustand";
import { dealsApi } from "@/api/deals-api";
import { transformDeals } from "@/services/deals-service";
import { Deal, DealTransformed } from "@/lib/schema/deal-schema";

interface DealsListStore {
  // State
  deals: Record<string, DealTransformed>;
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;

  // Actions
  fetchDeals: (workspaceId: string) => Promise<void>;
  updateDeal: (dealId: string, updates: Partial<DealTransformed>) => void;
  clearDeals: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  deals: {},
  isLoading: false,
  error: null,
  lastFetchTime: null,
};

export const useDealsListStore = create<DealsListStore>()((set) => ({
  ...initialState,

  fetchDeals: async (workspaceId: string) => {
    set({ isLoading: true, error: null });

    try {
      // Fetch simple deal list from API
      const rawDeals = await dealsApi.getSimpleDealList(workspaceId);

      // Transform the deals using the existing service
      const transformedDeals = transformDeals(rawDeals as Deal[]);

      set({
        deals: transformedDeals,
        isLoading: false,
        lastFetchTime: Date.now(),
      });
    } catch (error) {
      console.error("Error fetching deals:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to fetch deals",
        isLoading: false,
      });
    }
  },

  updateDeal: (dealId: string, updates: Partial<DealTransformed>) => {
    set((state) => ({
      deals: {
        ...state.deals,
        [dealId]: {
          ...state.deals[dealId],
          ...updates,
        },
      },
    }));
  },

  clearDeals: () => {
    set(initialState);
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },
}));

// Selectors
export const selectDeals = (state: DealsListStore) => state.deals;
export const selectIsLoading = (state: DealsListStore) => state.isLoading;
export const selectError = (state: DealsListStore) => state.error;
export const selectDealsList = (state: DealsListStore) =>
  Object.values(state.deals);
export const selectDealById = (dealId: string) => (state: DealsListStore) =>
  state.deals[dealId];
